/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { Package, CheckCircle } from 'lucide-react';
import { UserStory } from '../../types';
import { AcceptanceCriterion, AcceptanceCriterionStatus, ACCEPTANCE_CRITERION_STATUSES, SPECIAL_RELEASE_VALUES, DEFAULT_RELEASE_VALUE, normalizeAcceptanceCriteria } from '@shared/types/user-stories';
import type { Issue } from '@shared/types/issues';

interface UserStoryFormProps {
  formData: Partial<UserStory>;
  statusOptions: string[];
  releases?: Issue[];
  onInputChange: (field: keyof UserStory, value: any) => void;
  onAcceptanceCriteriaChange: (criteria: AcceptanceCriterion[]) => void;
  onLinksChange: (links: string[]) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
}

export function UserStoryForm({
  formData,
  statusOptions,
  releases = [],
  onInputChange,
  onAcceptanceCriteriaChange,
  onLinksChange,
  onSubmit,
  onCancel
}: UserStoryFormProps) {
  const handleACTextChange = (index: number, text: string) => {
    const currentAC = normalizeAcceptanceCriteria(formData.acceptanceCriteria);
    const newAC = [...currentAC];
    // Explicitly preserve all properties, update text
    const existingCriterion = newAC[index];
    newAC[index] = {
      text: text,
      release: existingCriterion.release,
      status: existingCriterion.status,
    };
    onAcceptanceCriteriaChange(newAC);
  };

  const handleACReleaseChange = (index: number, release: string) => {
    const currentAC = normalizeAcceptanceCriteria(formData.acceptanceCriteria);
    const newAC = [...currentAC];
    // Explicitly preserve all properties, set release based on selection
    const existingCriterion = newAC[index];
    newAC[index] = {
      text: existingCriterion.text,
      status: existingCriterion.status,
      // Store undefined if release is empty or the default "Unscheduled" value
      release: (release && release !== DEFAULT_RELEASE_VALUE) ? release : undefined,
    };
    onAcceptanceCriteriaChange(newAC);
  };

  const handleACStatusChange = (index: number, status: AcceptanceCriterionStatus) => {
    const currentAC = normalizeAcceptanceCriteria(formData.acceptanceCriteria);
    const newAC = [...currentAC];
    // Explicitly preserve all properties, update status
    const existingCriterion = newAC[index];
    newAC[index] = {
      text: existingCriterion.text,
      release: existingCriterion.release,
      status: status,
    };
    onAcceptanceCriteriaChange(newAC);
  };

  const getACStatusColor = (status: AcceptanceCriterionStatus | undefined): string => {
    switch (status) {
      case 'Accepted': return '#22c55e';
      case 'Ready For Review': return '#6f42c1';
      case 'Scoped': return '#0066cc';
      case 'Required':
      default: return '#6b7280';
    }
  };

  // Build release options: special values first, then actual releases
  const releaseOptions = [
    ...SPECIAL_RELEASE_VALUES,
    ...releases.map(r => r.id.split(':')[0].trim())
  ];

  const handleACAdd = () => {
    const currentAC = normalizeAcceptanceCriteria(formData.acceptanceCriteria);
    // New criteria default to "Unscheduled" release and "Required" status
    onAcceptanceCriteriaChange([...currentAC, { text: '', release: undefined, status: 'Required' }]);
  };

  const handleACRemove = (index: number) => {
    const currentAC = normalizeAcceptanceCriteria(formData.acceptanceCriteria);
    const newAC = currentAC.filter((_, i) => i !== index);
    onAcceptanceCriteriaChange(newAC);
  };

  return (
    <form onSubmit={onSubmit} style={{ 
      marginBottom: '24px', 
      padding: '20px', 
      border: '1px solid #ddd', 
      borderRadius: '8px',
      background: '#f8f9fa'
    }}>
      <h3 style={{ marginTop: 0 }}>Create New User Story</h3>
      
      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
          Title *
        </label>
        <input
          type="text"
          value={formData.title || ''}
          onChange={(e) => onInputChange('title', e.target.value)}
          required
          style={{
            width: '100%',
            padding: '8px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px'
          }}
        />
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
          Role (As a...) *
        </label>
        <input
          type="text"
          value={formData.role || ''}
          onChange={(e) => onInputChange('role', e.target.value)}
          required
          placeholder="e.g., Admin, Teacher, Learner"
          style={{
            width: '100%',
            padding: '8px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px'
          }}
        />
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
          Goal (I want to...) *
        </label>
        <textarea
          value={formData.goal || ''}
          onChange={(e) => onInputChange('goal', e.target.value)}
          required
          rows={3}
          style={{
            width: '100%',
            padding: '8px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px',
            fontFamily: 'inherit'
          }}
        />
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
          Benefit (So that...) *
        </label>
        <textarea
          value={formData.benefit || ''}
          onChange={(e) => onInputChange('benefit', e.target.value)}
          required
          rows={3}
          style={{
            width: '100%',
            padding: '8px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px',
            fontFamily: 'inherit'
          }}
        />
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
          Acceptance Criteria
        </label>
        {normalizeAcceptanceCriteria(formData.acceptanceCriteria).map((criterion, index) => (
          <div key={index} style={{ marginBottom: '12px', padding: '12px', backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '6px' }}>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
              <span style={{ color: '#666', paddingTop: '8px', minWidth: '20px' }}>{index + 1}.</span>
              <input
                type="text"
                value={criterion.text}
                onChange={(e) => handleACTextChange(index, e.target.value)}
                placeholder="Acceptance criterion text"
                style={{
                  flex: 1,
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
              <button
                type="button"
                onClick={() => handleACRemove(index)}
                className="btn btn-secondary"
                style={{ padding: '8px 16px', fontSize: '12px' }}
              >
                Remove
              </button>
            </div>
            <div style={{ display: 'flex', gap: '16px', marginLeft: '28px', alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <Package size={14} style={{ color: '#6f42c1' }} />
                <select
                  value={criterion.release || DEFAULT_RELEASE_VALUE}
                  onChange={(e) => handleACReleaseChange(index, e.target.value)}
                  style={{
                    padding: '6px 8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '13px',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    minWidth: '140px'
                  }}
                >
                  {releaseOptions.map(release => (
                    <option key={release} value={release}>{release}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <CheckCircle size={14} style={{ color: getACStatusColor(criterion.status) }} />
                <select
                  value={criterion.status || 'Required'}
                  onChange={(e) => handleACStatusChange(index, e.target.value as AcceptanceCriterionStatus)}
                  style={{
                    padding: '6px 8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '13px',
                    backgroundColor: 'white',
                    cursor: 'pointer'
                  }}
                >
                  {ACCEPTANCE_CRITERION_STATUSES.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={handleACAdd}
          className="btn"
          style={{ padding: '8px 16px', fontSize: '12px', marginTop: '8px' }}
        >
          Add Criteria
        </button>
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
          Status
        </label>
        <select
          value={formData.status || 'Not Started'}
          onChange={(e) => onInputChange('status', e.target.value)}
          style={{
            width: '100%',
            padding: '8px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px'
          }}
        >
          {statusOptions.map(status => (
            <option key={status} value={status}>{status}</option>
          ))}
        </select>
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
          Section
        </label>
        <input
          type="text"
          value={formData.section || ''}
          onChange={(e) => onInputChange('section', e.target.value)}
          placeholder="e.g., Admin User Stories, Curriculum Manager User Stories"
          style={{
            width: '100%',
            padding: '8px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px'
          }}
        />
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
          Epic ID
        </label>
        <input
          type="text"
          value={formData.epicId || ''}
          onChange={(e) => onInputChange('epicId', e.target.value)}
          placeholder="e.g., EPIC-001"
          style={{
            width: '100%',
            padding: '8px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px'
          }}
        />
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
          Links (comma-separated)
        </label>
        <input
          type="text"
          value={formData.links?.join(', ') || ''}
          onChange={(e) => {
            const value = e.target.value;
            const links = value ? value.split(',').map(l => l.trim()).filter(l => l) : [];
            onLinksChange(links);
          }}
          placeholder="e.g., BUG-001, TASK-042, https://docs.example.com"
          style={{
            width: '100%',
            padding: '8px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px'
          }}
        />
      </div>

      <div style={{ display: 'flex', gap: '12px' }}>
        <button 
          type="submit" 
          className="btn"
          style={{ padding: '8px 24px' }}
        >
          Create User Story
        </button>
        <button 
          type="button" 
          onClick={onCancel}
          className="btn btn-secondary"
          style={{ padding: '8px 24px' }}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

